import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { SimpleButton, ControlledCheckbox, ErrorMessage } from '@/src/ui';
import { ControlledDynamicTextEditor, isEditorContentEmpty } from '@/src/lib';
import { defaultMeta, type QtiStimulusQuestionForEditorDTO } from '../../models/questionDTOs';
import {
    Debugger,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';
import { saveQuestion } from '../../services';
import { renderLoaders } from '../../utils';
import { Constants } from '@/src/utils';
import QuestionSelector from './components/QuestionSelector';
import { metaSchema } from '../../models/schema';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const config = {
    minQuestions: 2,
    maxQuestions: 10,
};

const schema = yup.object().shape({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test(
            'not-empty',
            'Prompt cannot be empty.',
            (value) => !isEditorContentEmpty(value || '')
        ),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (schema) =>
            schema
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty.',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (schema) => schema.optional(),
    }),
    selectedQuestions: yup.array().min(config.minQuestions).max(config.maxQuestions),
    meta: metaSchema.required(),
});

interface StimulusEditorProps {
    initialModel?: QtiStimulusQuestionForEditorDTO;
}

export default function StimulusEditor({ initialModel }: StimulusEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);
    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const defaultValues: QtiStimulusQuestionForEditorDTO = initialModel
        ? {
              ...initialModel,
              meta: {
                  ...defaultMeta,
                  ...initialModel.meta,
              },
          }
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              selectedQuestions: [],
              meta: {
                  ...defaultMeta,
                  questionTypeMasterId: qtm.STIMULUS,
                  gradeLevelMasterIds: [1],
              }, // non-mutable for stim, but kept in model for compatibility with BaseQtiQuestionDTO
          };

    const [formContext, setFormContext] = useState({
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiStimulusQuestionForEditorDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();
    useEffect(() => {
        setFormContext({ isSpanishAllowed: question.meta.isSpanishAllowed });
    }, [question.meta.isSpanishAllowed]);

    const selectedQuestions = watch('selectedQuestions');

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            key={`StimulusEditor-${question.meta.isSpanishAllowed}`}
            className="p-4 space-y-4"
            onSubmit={handleSubmit(onSubmit)}
        >
            {/* Handles all Spanish entry */}
            {modalProps && (
                <TextEntryModal
                    initialData={modalProps.initialData}
                    fieldName={modalProps.fieldName}
                    friendlyName={modalProps.friendlyName}
                    textMode={modalProps.textMode}
                    error={modalProps.error}
                    control={control}
                    handleClose={() => setModalProps(null)}
                />
            )}
            {renderLoaders(loading, success, error, isEdit)}
            <h2>Stimulus Title:</h2>
            <QuestionTitleField
                id={question.id}
                title={question.title}
                control={control}
                setIsTitleError={setIsTitleError}
                error={errors.title}
            />

            <ControlledCheckbox
                name="meta.isSpanishAllowed"
                label="Is Spanish Allowed"
                onChange={(e, field) => {
                    const checked = e.target.checked;
                    field.onChange(checked);
                }}
                disabled={question.selectedQuestions.length > 0}
                control={control}
            />
            {question.selectedQuestions.length > 0 && (
                <ErrorMessage
                    error="Spanish toggle disabled while questions are selected."
                    color="yellow"
                />
            )}
            <ErrorMessage error={errors.meta?.isSpanishAllowed} />

            <div className="my-4" />

            <div className="flex flex-row gap-4">
                <h2>Prompt:</h2>
                {question.meta.isSpanishAllowed && (
                    <div
                        onClick={() =>
                            setModalProps({
                                initialData: question.prompt_es,
                                fieldName: 'prompt_es',
                                friendlyName: 'Prompt (Spanish)',
                                textMode: 'rich',
                                error: errors.prompt_es?.message,
                            })
                        }
                        style={{ cursor: 'pointer' }}
                    >
                        Edit Spanish
                    </div>
                )}
                <ErrorMessage error={errors.prompt_es} />
            </div>

            <ControlledDynamicTextEditor
                name="prompt"
                control={control}
                initialValue={question.prompt}
                style={{ minHeight: '150px' }}
            />
            <ErrorMessage error={errors.prompt} />

            <div className="my-4" />

            <QuestionSelector setValue={setValue} selectedQuestions={selectedQuestions} />

            <div className="my-4" />

            <div className="flex items-center space-x-2">
                <div>
                    <SimpleButton
                        type="submit"
                        color="primary"
                        className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                        disabled={isSubmitting || loading}
                    >
                        {isEdit ? 'Update Question' : 'Create Question'}
                    </SimpleButton>
                </div>
            </div>
            <ErrorMessage
                error={Object.keys(errors).length ? 'Please correct the errors to continue' : ''}
            />
            {renderLoaders(loading, success, error, isEdit)}

            <Debugger question={question} errors={errors} />
        </form>
    );
}
